import React from 'react'
import { Typography, Grid, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ColStack } from 'components/atoms/container/InCardStack'
import NotificationBanner from 'components/atoms/NotificationBanner'
import { getLocalUser } from 'utils/localStorage'
import RowStack from 'components/atoms/container/RowStack'
import { formatDate } from 'utils/formatDateTime'
import { permitted, isAllowedFeature } from 'utils/permissionCheck'
import { PERMISSION_LEVEL } from 'routes/constants'
import { FEATURE_KEYS } from 'services/constants'
import { ContactMonark } from 'components/atoms/Text'
import SectionDivider from 'components/atoms/SectionDivider'
import {
  BoxSection,
  Section,
  SectionWithDesc
} from 'components/molecules/user/SettingsMolecules'
import moment from 'moment'
import { useChannelIOApi } from 'react-channel-plugin'

const BillingSettings = () => {
  const { t } = useTranslation()
  const { showMessenger } = useChannelIOApi()
  const userInfo = getLocalUser()
  const hasGoalContentFeature = isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT)
  const hasLicense = permitted(PERMISSION_LEVEL.LICENSED)
  const isAdmin =
    permitted(PERMISSION_LEVEL.SUPER_ADMIN) ||
    permitted(PERMISSION_LEVEL.ADMINISTRATOR) ||
    permitted(PERMISSION_LEVEL.COACH)
  const hasOneOnOneFeature = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)
  const licenseExpired = moment
    .utc(userInfo?.organization?.licenseExpireAt)
    .isBefore(moment.utc())

  const eyebrowTextProps = {
    variant: 'eyebrow',
    fontWeight: 600
  }

  const italicTextProps = {
    variant: 'body1KindaSmall',
    color: 'textSecondary',
    fontStyle: 'italic'
  }

  return (
    <ColStack spacing={12}>
      <NotificationBanner notificationText={t('billing.oneOnOneIsBeta')} />
      <SectionDivider />

      <Section title={t('billing.yourPlan')}>
        <Grid container spacing={6} width='100%'>
          <BoxSection heading={t('billing.monarkPlatform')}>
            <ColStack spacing={8}>
              {hasGoalContentFeature ? (
                <>
                  <ColStack spacing={2}>
                    <Typography
                      {...eyebrowTextProps}
                      color={licenseExpired ? 'error.main' : 'textPrimary'}>
                      {t(
                        `billing.${licenseExpired ? 'expiredOn' : 'licensedUntil'}`
                      )}
                    </Typography>
                    <RowStack justifyContent='space-between'>
                      <Typography variant='h4' flex={1}>
                        {formatDate(userInfo?.organization?.licenseExpireAt)}
                      </Typography>
                      <Typography
                        sx={{
                          lineHeight: '140%',
                          textAlign: 'end',
                          wordBreak: 'break-all',
                          flex: 1
                        }}>
                        {userInfo?.organization?.name}
                      </Typography>
                    </RowStack>
                  </ColStack>
                  <ContactMonark />
                </>
              ) : (
                <>
                  <Typography {...eyebrowTextProps}>
                    {t('billing.featureUnavailable')}
                  </Typography>

                  {hasLicense ? (
                    <>
                      <Typography {...italicTextProps}>
                        {t('billing.organizationNotEnabledFeature')}
                      </Typography>

                      {isAdmin && <ContactMonark />}
                    </>
                  ) : (
                    <Typography {...italicTextProps}>
                      {t('billing.orgNotValidLicense')}
                    </Typography>
                  )}
                </>
              )}
            </ColStack>
          </BoxSection>

          <BoxSection
            heading={t('billing.oneOnOneMeetings')}
            headTag={t('headers.tags.beta')}>
            <ColStack spacing={8}>
              {hasOneOnOneFeature || isAdmin ? (
                <RowStack justifyContent='space-between'>
                  <ColStack spacing={2} flex={1}>
                    <Typography {...eyebrowTextProps}>
                      {t('billing.freeBeta')}
                    </Typography>
                    <Typography>{t('billing.untilFurtherNotice')}</Typography>
                  </ColStack>
                  {isAdmin && !hasOneOnOneFeature && showMessenger ? (
                    <Button
                      size='small'
                      color='primary'
                      onClick={() => showMessenger()}>
                      {t('billing.tryNow')}
                    </Button>
                  ) : null}
                </RowStack>
              ) : (
                <Typography {...eyebrowTextProps}>
                  {t('billing.featureUnavailable')}
                </Typography>
              )}

              <Typography {...italicTextProps}>
                {hasOneOnOneFeature
                  ? t('billing.switchToPaidPlan')
                  : isAdmin
                    ? t('billing.oneOnOneInBeta')
                    : t('billing.organizationNotEnabledFeature')}
              </Typography>
            </ColStack>
          </BoxSection>
        </Grid>
      </Section>

      <SectionWithDesc
        title={t('billing.payment')}
        description={t('billing.noPaymentRequired')}
      />

      <SectionWithDesc
        title={t('billing.invoices')}
        description={
          isAdmin ? (
            <ContactMonark
              i18nKey={'billing.contactMonarkSupport'}
              {...eyebrowTextProps}
              color='primary.main'
              fontStyle='normal'
            />
          ) : (
            t('billing.noInvoicesAvailable')
          )
        }
      />
    </ColStack>
  )
}

export default BillingSettings
